import { JSONEditor } from '@json-editor/json-editor'
import Choices from 'choices.js'
import _ from 'lodash'

class SelectChoices extends JSONEditor.AbstractEditor {
  preBuild () {
    this.value = []

    // Check if "Select All" option should be shown (default: false)
    this.showSelectAll = this.options.showSelectAll || false
    if (this.schema.enumSource) {
      this.enumSource = []

      this.enumSource = _.map(this.schema.enumSource, (source) => {
        if (typeof source === 'string') {
          return { source }
        } else if (!Array.isArray(source)) {
          return _.clone(source)
        } else {
          return source
        }
      })

      _.each(this.enumSource, (source) => {
        if (source.value) {
          source.value = this.jsoneditor.compileTemplate((source.value), this.template_engine)
        }
        if (source.title) {
          source.title = this.jsoneditor.compileTemplate((source.title), this.template_engine)
        }
        if (source.filter) {
          source.filter = this.jsoneditor.compileTemplate((source.filter), this.template_engine)
        }
      })
    }
  }

  build () {
    this.container.classList.add('multi-select')
    this.header = document.createElement('span')
    this.header.textContent = this.getTitle()
    this.title = this.theme.getHeader(this.header)
    this.container.appendChild(this.title)

    this.input = this.theme.getSelectInput()
    this.input.setAttribute('multiple', 'true')
    this.input.addEventListener('change', (e) => {
      this.refreshValue()
    })

    if (this.schema.description) {
      this.description = this.theme.getDescription(this.translateProperty(this.schema.description))
      this.container.appendChild(this.description)
    }

    this.container.appendChild(this.input)

    this.choices = new Choices(this.input, {
      maxItemCount: -1,
      removeItemButton: true,
      duplicateItemsAllowed: false,
      paste: false,
      editItems: false,
      shouldSort: false,
      callbackOnCreateTemplates: function (template) {
        return {
          item: (classNames, data) => {
            // If group exists, prepend it to the label
            const label = data.customProperties && data.customProperties.group
              ? `${data.customProperties.group}: ${data.label}`
              : data.label
            return template(`
                            <div class="${classNames.item} ${data.highlighted ? classNames.highlightedState : classNames.itemSelectable}" data-item data-id="${data.id}" data-value="${data.value}" ${data.active ? 'aria-selected="true"' : ''} ${data.disabled ? 'aria-disabled="true"' : ''}>
                                ${label}
                                <button type="button" class="${classNames.button}" data-button>Remove item</button>
                            </div>
                        `)
          }
        }
      }
    })
    this.choices._handleBackspace = function(items) {
      if (!this.input.value) {
        return;
      }
    }
  }

  postBuild () {
    super.postBuild()
  }

  setValue (value, initial) {
    this.value = Array.isArray(value) ? value : []
    this.onChange(true)
  }

  refreshValue () {
    this.value = this.choices.getValue(true)
    this.onChange(true)
  }

  onWatchedFieldChange () {
    if (this.enumSource) {
      const vars = this.getWatchedFieldValues()
      const itemDisabled = []
      if (!vars.val) return

      const items = vars.val.flatMap(group => {
        // Dynamically find the title property (string that starts with $_ or is named 'title')
        const titleKey = group.$_title || group.$_filterTitle

        const title = {
          value: '',
          title: titleKey,
          disabled: true
        }

        // Dynamically find the array property in the group object
        const tagArrayKey = Object.keys(group).find(key =>
          Array.isArray(group[key]) && key !== titleKey
        )
        const tags = tagArrayKey ? group[tagArrayKey] : []

        const value = tags.map(tag => ({
          value: tag,
          title: tag,
          disabled: false
        }))
        return [title, ...value]
      })
      const itemTitles = []
      const itemValues = []
      for (let j = 0; j < items.length; j++) {
        const item = items[j]
        if (item && typeof item === 'object' && 'value' in item && 'title' in item && this.options.useMultiple) {
          itemValues[j] = item.value
          itemTitles[j] = item.title
          itemDisabled[j] = !!item.disabled
        } else {
          /* Rendered value */
          if (this.enumSource[i].value) {
            itemValues[j] = this.typecast(this.enumSource[i].value({
              i: j,
              item
            }))
            /* Use value directly */
          } else {
            itemValues[j] = items[j]
          }

          /* Rendered title */
          if (this.enumSource[i].title) {
            itemTitles[j] = this.enumSource[i].title({
              i: j,
              item
            })
            /* Use value as the title also */
          } else {
            itemTitles[j] = itemValues[j]
          }
          itemDisabled[j] = false
        }
      }
      const selectOptions = itemValues
      const selectTitles = itemTitles
      const joinedTotals = []
      let currentGroup = ''
      const allValidValues = [...selectOptions] // Start with all regular options

      for (let k = 0; k < selectOptions.length; k++) {
        // If disabled, it's a group header
        if (itemDisabled[k]) {
          currentGroup = selectTitles[k]
          joinedTotals.push({
            value: selectOptions[k],
            title: selectTitles[k],
            disabled: true,
            selected: false,
            group: null,
            depth: 0
          })
          // Only add "Select All" option if showSelectAll is true
          if (this.showSelectAll) {
            const selectAllKey = `*${selectTitles[k]}`
            allValidValues.push(selectAllKey) // Add "Select All" key to valid values
            joinedTotals.push({
              value: selectAllKey,
              title: `Select All for: ${selectTitles[k]}`,
              disabled: false,
              selected: false,
              group: null,
              depth: 0
            })
          }
        } else {
          joinedTotals.push({
            value: selectOptions[k],
            title: selectTitles[k],
            disabled: false,
            selected: false,
            group: currentGroup, // Track group for each tag
            depth: 1
          })
        }
      }
      this.choices.clearStore()
      this.choices.setChoices(
        joinedTotals.map(choice => ({
          value: choice.value,
          label: choice.title,
          disabled: choice.disabled,
          selected: false,
          customProperties: { group: choice.group }
        })),
        'value',
        'label',
        true
      )
      // Set selected values after setting choices
      if (Array.isArray(this.value)) {
        this.value.forEach(val => {
          this.choices.setChoiceByValue(val)
        })
      }
      const values = allValidValues // All valid option values including "Select All" keys
      const currentValue = this.value
      const cleanValue = Array.isArray(currentValue)
        ? currentValue.filter(v => values.includes(v))
        : []

      if (!_.isEqual(currentValue, cleanValue)) {
        this.value = cleanValue
        this.onChange(true)
        if (this.parent) {
          this.parent.onChildEditorChange(this)
        } else {
          this.jsoneditor.onChange()
        }
        this.jsoneditor.notifyWatchers(this.path)
      }
    }

    super.onWatchedFieldChange()
  }
}

export default SelectChoices
